import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { 
  Typography, TextField, Button, Box, Grid, CircularProgress, Card, CardContent, Stack, Tabs, Tab, Switch, FormControlLabel
} from '@mui/material';
import { useSnackbar } from 'notistack';
import sucVentaService, { SucVenta } from '../../services/sucVentaService';

const defaultForm: SucVenta = {
  nroSuc: 0,
  nroTipoFormulario: '',
  formuA: 0,
  formuB: 0,
  formuC: 0,
  formuN: 0,
  numPropio: '',
  nroTipoFormularioNumerador: '',
  copias: 1,
  renglones: 10,
  plantiA: '',
  plantiB: '',
  plantiC: '',
  plantiN: '',
  plantiComun: '',
  impA: '',
  impB: '',
  impC: '',
  impN: '',
  impComun: '',
  vpreviaA: 'N',
  vpreviaB: 'N',
  vpreviaC: 'N',
  vpreviaN: 'N',
  vpreviaComun: 'N',
  imprime: 'N',
  liquidaComision: 'N',
  afectaLimCred: 'N',
  actualizaLimCred: 'N',
  tipoImpresora: '',
  usaCodigoBarra: 0,
  tipoRenglones: '',
  idWsfe: '',
  generaRemito: 'N',
  nroSucRemito: 0,
  soloAdvertencia: 0,
  usaLetraM: 0,
  idWsfecred: '',
  passEmision: '',
  passAnulacion: '',
  generaOcarga: 'N',
  nroSucOcarga: 0
};

const SucVentaForm: React.FC = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { nroSuc, nroTipoFormulario } = useParams();
  const [formData, setFormData] = useState<SucVenta>({ ...defaultForm });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [currentTab, setCurrentTab] = useState(0);

  const loadSucVenta = useCallback(async () => {
    if (!nroSuc || !nroTipoFormulario || nroSuc === 'nuevo' || nroTipoFormulario === 'nuevo') return;
    try {
      setLoading(true);
      const sucVenta = await sucVentaService.getSucVenta(Number(nroSuc), nroTipoFormulario);
      setFormData(sucVenta);
    } catch (error) {
      enqueueSnackbar('Error al cargar la asignación', { variant: 'error' });
      navigate('/configuraciones/sucventa');
    } finally {
      setLoading(false);
    }
  }, [nroSuc, nroTipoFormulario, enqueueSnackbar, navigate]);

  useEffect(() => {
    if (nroSuc && nroSuc !== 'nuevo' && nroTipoFormulario && nroTipoFormulario !== 'nuevo') loadSucVenta();
  }, [nroSuc, nroTipoFormulario, loadSucVenta]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    if (['nroSuc', 'formuA', 'formuB', 'formuC', 'formuN', 'copias', 'renglones', 'usaCodigoBarra', 'nroSucRemito', 'soloAdvertencia', 'usaLetraM', 'nroSucOcarga'].includes(name)) {
      setFormData(prev => ({ ...prev, [name]: Number(value) }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleSwitchChange = (name: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({ ...prev, [name]: e.target.checked ? 'S' : 'N' }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setSaving(true);
      
      if (!formData.nroTipoFormulario.trim()) {
        enqueueSnackbar('El tipo de formulario es requerido', { variant: 'warning' });
        return;
      }

      if (nroSuc && nroSuc !== 'nuevo' && nroTipoFormulario && nroTipoFormulario !== 'nuevo') {
        await sucVentaService.updateSucVenta(Number(nroSuc), nroTipoFormulario, formData);
      } else {
        await sucVentaService.createSucVenta(formData);
      }

      enqueueSnackbar('Asignación guardada exitosamente', { variant: 'success' });
      navigate('/configuraciones/sucventa');
    } catch (error: any) {
      console.error('Error al guardar asignación:', error);
      enqueueSnackbar(error?.response?.data?.message || 'Error al guardar la asignación', { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      <Card sx={{ bgcolor: '#ffffff', borderRadius: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.1)', overflow: 'hidden' }}>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Typography variant="h4" sx={{ mb: 3, fontWeight: 600, color: '#1976d2' }}>
            {nroSuc && nroSuc !== 'nuevo' && nroTipoFormulario && nroTipoFormulario !== 'nuevo' ? 'Editar Asignación' : 'Nueva Asignación'}
          </Typography>

          <Tabs value={currentTab} onChange={(e, v) => setCurrentTab(v)} sx={{ mb: 3 }}>
            <Tab label="General" />
            <Tab label="Numeración" />
            <Tab label="Impresión" />
            <Tab label="Configuración" />
          </Tabs>

          <form onSubmit={handleSubmit}>
            {currentTab === 0 && (
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <TextField 
                    name="nroSuc" 
                    label="N° Sucursal" 
                    type="number"
                    value={formData.nroSuc} 
                    onChange={handleChange} 
                    fullWidth 
                    required 
                    disabled={!!(nroSuc && nroSuc !== 'nuevo')}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField 
                    name="nroTipoFormulario" 
                    label="N° Tipo Formulario" 
                    value={formData.nroTipoFormulario} 
                    onChange={handleChange} 
                    fullWidth 
                    required
                    disabled={!!(nroTipoFormulario && nroTipoFormulario !== 'nuevo')}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField 
                    name="copias" 
                    label="Copias" 
                    type="number"
                    value={formData.copias || 1} 
                    onChange={handleChange} 
                    fullWidth
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField 
                    name="renglones" 
                    label="Renglones" 
                    type="number"
                    value={formData.renglones || 10} 
                    onChange={handleChange} 
                    fullWidth
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControlLabel
                    control={<Switch checked={formData.imprime === 'S'} onChange={handleSwitchChange('imprime')} />}
                    label="Imprime"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControlLabel
                    control={<Switch checked={formData.liquidaComision === 'S'} onChange={handleSwitchChange('liquidaComision')} />}
                    label="Liquida Comisión"
                  />
                </Grid>
              </Grid>
            )}

            {currentTab === 1 && (
              <Grid container spacing={3}>
                <Grid item xs={12} md={3}>
                  <TextField 
                    name="formuA" 
                    label="Formulario A" 
                    type="number"
                    value={formData.formuA || 0} 
                    onChange={handleChange} 
                    fullWidth
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField 
                    name="formuB" 
                    label="Formulario B" 
                    type="number"
                    value={formData.formuB || 0} 
                    onChange={handleChange} 
                    fullWidth
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField 
                    name="formuC" 
                    label="Formulario C" 
                    type="number"
                    value={formData.formuC || 0} 
                    onChange={handleChange} 
                    fullWidth
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField 
                    name="formuN" 
                    label="Formulario N" 
                    type="number"
                    value={formData.formuN || 0} 
                    onChange={handleChange} 
                    fullWidth
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField 
                    name="numPropio" 
                    label="Numeración Propia" 
                    value={formData.numPropio || ''} 
                    onChange={handleChange} 
                    fullWidth
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField 
                    name="nroTipoFormularioNumerador" 
                    label="Tipo Form. Numerador" 
                    value={formData.nroTipoFormularioNumerador || ''} 
                    onChange={handleChange} 
                    fullWidth
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Grid>
              </Grid>
            )}

            {currentTab === 2 && (
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <TextField 
                    name="plantiA" 
                    label="Plantilla A" 
                    value={formData.plantiA || ''} 
                    onChange={handleChange} 
                    fullWidth
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField 
                    name="impA" 
                    label="Impresora A" 
                    value={formData.impA || ''} 
                    onChange={handleChange} 
                    fullWidth
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField 
                    name="plantiB" 
                    label="Plantilla B" 
                    value={formData.plantiB || ''} 
                    onChange={handleChange} 
                    fullWidth
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField 
                    name="impB" 
                    label="Impresora B" 
                    value={formData.impB || ''} 
                    onChange={handleChange} 
                    fullWidth
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField 
                    name="tipoImpresora" 
                    label="Tipo Impresora" 
                    value={formData.tipoImpresora || ''} 
                    onChange={handleChange} 
                    fullWidth
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControlLabel
                    control={<Switch checked={formData.usaCodigoBarra === 1} onChange={(e) => setFormData(prev => ({ ...prev, usaCodigoBarra: e.target.checked ? 1 : 0 }))} />}
                    label="Usa Código de Barras"
                  />
                </Grid>
              </Grid>
            )}

            {currentTab === 3 && (
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <FormControlLabel
                    control={<Switch checked={formData.afectaLimCred === 'S'} onChange={handleSwitchChange('afectaLimCred')} />}
                    label="Afecta Límite de Crédito"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControlLabel
                    control={<Switch checked={formData.actualizaLimCred === 'S'} onChange={handleSwitchChange('actualizaLimCred')} />}
                    label="Actualiza Límite de Crédito"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControlLabel
                    control={<Switch checked={formData.generaRemito === 'S'} onChange={handleSwitchChange('generaRemito')} />}
                    label="Genera Remito"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField 
                    name="nroSucRemito" 
                    label="N° Suc. Remito" 
                    type="number"
                    value={formData.nroSucRemito || 0} 
                    onChange={handleChange} 
                    fullWidth
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControlLabel
                    control={<Switch checked={formData.generaOcarga === 'S'} onChange={handleSwitchChange('generaOcarga')} />}
                    label="Genera Orden de Carga"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField 
                    name="nroSucOcarga" 
                    label="N° Suc. O. Carga" 
                    type="number"
                    value={formData.nroSucOcarga || 0} 
                    onChange={handleChange} 
                    fullWidth
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField 
                    name="idWsfe" 
                    label="ID WSFE" 
                    value={formData.idWsfe || ''} 
                    onChange={handleChange} 
                    fullWidth
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField 
                    name="idWsfecred" 
                    label="ID WSFE Cred" 
                    value={formData.idWsfecred || ''} 
                    onChange={handleChange} 
                    fullWidth
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Grid>
              </Grid>
            )}

            <Stack direction="row" spacing={2} sx={{ mt: 4 }}>
              <Button 
                type="submit" 
                variant="contained" 
                color="primary" 
                disabled={saving}
                sx={{ borderRadius: 2, px: 4, py: 1.5, textTransform: 'none', fontWeight: 500 }}
              >
                {saving ? <CircularProgress size={24} /> : 'Guardar'}
              </Button>
              <Button 
                variant="outlined" 
                onClick={() => navigate('/configuraciones/sucventa')} 
                disabled={saving}
                sx={{ borderRadius: 2, px: 4, py: 1.5, textTransform: 'none', fontWeight: 500 }}
              >
                Cancelar
              </Button>
            </Stack>
          </form>
        </CardContent>
      </Card>
    </Box>
  );
};

export default SucVentaForm;
